/**
 * PluggrManager.ts - Core plugin management system
 * 
 * CHANGE HISTORY:
 * - 251101_1504 [pending] danielbeer: Initial implementation with dynamic loading for FMwvTool
 * 
 * ARCHITECTURE NOTE: Dynamic Plugin Loading
 * 
 * This implementation supports loading plugins from:
 * 1. URLs (for FMwvTool custom modules without re-issuing)
 * 2. Files (local development)
 * 3. NPM packages (standard distribution)
 * 4. Plugin registry (future marketplace)
 * 
 * Key features:
 * - Sandboxed execution via PluginContext
 * - Conflict detection before installation
 * - Dependency management
 * - Permission system integration with Permittr
 * - Audit logging via Loggr
 * 
 * See: /packages/pluggr/ARCHITECTURE.md for complete design
 */

import type {
  Plugin,
  PluginManifest,
  PluginConfig,
  PluginContext,
  PluginRegistry,
  PluginConflict,
  PluginLoadOptions,
  PluginInstallOptions
} from './PluggrTypes';

export class PluggrManager {
  private registry: PluginRegistry = {
    plugins: new Map(),
    configs: new Map(),
    manifests: new Map()
  };
  
  private contextFactory: (pluginId: string) => PluginContext;
  
  constructor(contextFactory: (pluginId: string) => PluginContext) {
    this.contextFactory = contextFactory;
  }
  
  /**
   * Register a function (backward compatibility stub)
   */
  registerFunction(name: string, fn: Function): void {
    console.log(`[Pluggr] registerFunction called for ${name} - stub implementation`);
    // This is a stub for backward compatibility
    // Actual function registration should happen through plugin context
  }
  
  /**
   * Load plugin from various sources
   */
  async loadPlugin(options: PluginLoadOptions): Promise<Plugin> {
    switch (options.source) {
      case 'url':
        return await this.loadFromURL(options.url!);
      case 'file':
        return await this.loadFromFile(options.path!);
      case 'npm':
        return await this.loadFromNPM(options.package!, options.version);
      case 'registry':
        return await this.loadFromRegistry(options.package!, options.version);
      default:
        throw new Error(`Unsupported plugin source: ${options.source}`);
    }
  }
  
  /**
   * Load plugin from URL (for FMwvTool custom modules)
   */
  private async loadFromURL(url: string): Promise<Plugin> {
    try {
      // Fetch plugin code
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch plugin: ${response.statusText}`);
      }
      
      const code = await response.text();
      
      // Create isolated module scope
      const module = { exports: {} };
      const require = (name: string) => {
        throw new Error(`Dynamic require not supported: ${name}`);
      };
      
      // Execute plugin code in isolated scope
      const func = new Function('module', 'exports', 'require', code);
      func(module, module.exports, require);
      
      // Extract plugin
      const plugin = module.exports as Plugin;
      
      if (!plugin.manifest) {
        throw new Error('Plugin must export manifest');
      }
      
      return plugin;
    } catch (error) {
      throw new Error(`Failed to load plugin from URL: ${(error as Error).message}`);
    }
  }
  
  /**
   * Load plugin from file
   */
  private async loadFromFile(path: string): Promise<Plugin> {
    // For browser environments, this would use FileReader
    // For Node.js, this would use fs.readFile
    throw new Error('File loading not yet implemented');
  }
  
  /**
   * Load plugin from NPM package
   */
  private async loadFromNPM(packageName: string, version?: string): Promise<Plugin> {
    // Would use dynamic import or fetch from unpkg.com
    throw new Error('NPM loading not yet implemented');
  }
  
  /**
   * Load plugin from registry
   */
  private async loadFromRegistry(pluginId: string, version?: string): Promise<Plugin> {
    // Would fetch from OpCentral plugin registry
    throw new Error('Registry loading not yet implemented');
  }
  
  /**
   * Install plugin
   */
  async install(pluginId: string, options: PluginInstallOptions = {}): Promise<void> {
    // Check for conflicts
    const conflicts = await this.checkConflicts(pluginId);
    if (conflicts.some(c => c.severity === 'error')) {
      throw new Error(`Cannot install ${pluginId}: conflicts detected`);
    }
    
    // Load plugin (would determine source automatically)
    // For now, assume it's already loaded
    const plugin = this.registry.plugins.get(pluginId);
    if (!plugin) {
      throw new Error(`Plugin ${pluginId} not found`);
    }
    
    // Install dependencies if requested
    if (options.installDependencies && plugin.manifest.dependencies) {
      for (const [depId, depVersion] of Object.entries(plugin.manifest.dependencies)) {
        if (!this.registry.plugins.has(depId)) {
          await this.install(depId, { version: depVersion });
        }
      }
    }
    
    // Create default config
    const config: PluginConfig = {
      enabled: true,
      settings: {},
      autoUpdate: false,
      priority: 0
    };
    
    this.registry.configs.set(pluginId, config);
  }
  
  /**
   * Register plugin (after loading)
   */
  async register(plugin: Plugin): Promise<void> {
    const { id } = plugin.manifest;
    
    // Store plugin and manifest
    this.registry.plugins.set(id, plugin);
    this.registry.manifests.set(id, plugin.manifest);
    
    // Create default config if not exists
    if (!this.registry.configs.has(id)) {
      this.registry.configs.set(id, {
        enabled: false,
        settings: {},
        autoUpdate: false,
        priority: 0
      });
    }
  }
  
  /**
   * Enable plugin
   */
  async enable(pluginId: string): Promise<void> {
    const plugin = this.registry.plugins.get(pluginId);
    const config = this.registry.configs.get(pluginId);
    
    if (!plugin || !config) {
      throw new Error(`Plugin ${pluginId} not found`);
    }
    
    // Create context for plugin
    const context = this.contextFactory(pluginId);
    
    // Call onload
    if (plugin.onload) {
      await plugin.onload(context);
    }
    
    // Update config
    config.enabled = true;
    this.registry.configs.set(pluginId, config);
  }
  
  /**
   * Disable plugin
   */
  async disable(pluginId: string): Promise<void> {
    const plugin = this.registry.plugins.get(pluginId);
    const config = this.registry.configs.get(pluginId);
    
    if (!plugin || !config) {
      throw new Error(`Plugin ${pluginId} not found`);
    }
    
    // Call onunload
    if (plugin.onunload) {
      await plugin.onunload();
    }
    
    // Update config
    config.enabled = false;
    this.registry.configs.set(pluginId, config);
  }
  
  /**
   * Uninstall plugin
   */
  async uninstall(pluginId: string): Promise<void> {
    // Disable first
    const config = this.registry.configs.get(pluginId);
    if (config?.enabled) {
      await this.disable(pluginId);
    }
    
    // Remove from registry
    this.registry.plugins.delete(pluginId);
    this.registry.configs.delete(pluginId);
    this.registry.manifests.delete(pluginId);
  }
  
  /**
   * Check for conflicts
   */
  async checkConflicts(pluginId: string): Promise<PluginConflict[]> {
    const conflicts: PluginConflict[] = [];
    const manifest = this.registry.manifests.get(pluginId);
    
    if (!manifest) {
      return conflicts;
    }
    
    // Check for feature overlaps
    if (manifest.features) {
      for (const [existingId, existingManifest] of this.registry.manifests.entries()) {
        if (existingId === pluginId) continue;
        
        const overlappingFeatures = manifest.features.filter(f =>
          existingManifest.features?.includes(f)
        );
        
        if (overlappingFeatures.length > 0) {
          conflicts.push({
            type: 'feature_overlap',
            severity: 'warning',
            plugins: [pluginId, existingId],
            description: `Both plugins provide: ${overlappingFeatures.join(', ')}`,
            resolution: 'Choose one plugin or configure priority'
          });
        }
      }
    }
    
    // Check for dependency conflicts
    if (manifest.dependencies) {
      for (const [depId, depVersion] of Object.entries(manifest.dependencies)) {
        const existingManifest = this.registry.manifests.get(depId);
        if (existingManifest && existingManifest.version !== depVersion) {
          conflicts.push({
            type: 'dependency_conflict',
            severity: 'error',
            plugins: [pluginId, depId],
            description: `Version mismatch: requires ${depVersion}, found ${existingManifest.version}`,
            resolution: 'Update dependency or use compatible version'
          });
        }
      }
    }
    
    return conflicts;
  }
  
  /**
   * Get all plugins
   */
  list(filter?: { status?: 'enabled' | 'disabled' }): Array<{ plugin: Plugin; config: PluginConfig }> {
    const result: Array<{ plugin: Plugin; config: PluginConfig }> = [];
    
    for (const [id, plugin] of this.registry.plugins.entries()) {
      const config = this.registry.configs.get(id);
      if (!config) continue;
      
      if (filter?.status) {
        if (filter.status === 'enabled' && !config.enabled) continue;
        if (filter.status === 'disabled' && config.enabled) continue;
      }
      
      result.push({ plugin, config });
    }
    
    return result;
  }
  
  /**
   * Get plugin by ID
   */
  get(pluginId: string): { plugin: Plugin; config: PluginConfig } | null {
    const plugin = this.registry.plugins.get(pluginId);
    const config = this.registry.configs.get(pluginId);
    
    if (!plugin || !config) {
      return null;
    }
    
    return { plugin, config };
  }
  
  /**
   * Update plugin settings
   */
  updateSettings(pluginId: string, settings: Record<string, any>): void {
    const config = this.registry.configs.get(pluginId);
    if (!config) {
      throw new Error(`Plugin ${pluginId} not found`);
    }
    
    config.settings = { ...config.settings, ...settings };
    this.registry.configs.set(pluginId, config);
  }
}

// Singleton instance
let pluggrInstance: PluggrManager | null = null;

export function getPluggr(contextFactory?: (pluginId: string) => PluginContext): PluggrManager {
  if (!pluggrInstance && contextFactory) {
    pluggrInstance = new PluggrManager(contextFactory);
  }
  
  if (!pluggrInstance) {
    throw new Error('Pluggr not initialized. Call getPluggr with contextFactory first.');
  }
  
  return pluggrInstance;
}
